import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Tag, Pencil, Trash2, Copy, Calendar, CheckCircle, DollarSign, Percent } from "lucide-react";
import { ViewOnlyBanner } from "./ViewOnlyBanner";

export const PromoCodesTab = ({
  darkMode, promoCodes, adminSearch, formatPrice,
  openEditPromo, handleDeletePromo, copyPromoCode,
  setEditingPromo, setPromoForm, setPromoDialogOpen, initialPromoForm, viewOnly
}) => {
  const cardBg = darkMode ? "bg-gray-800/80 border border-gray-700/50" : "";

  return (
    <>
    {viewOnly && <ViewOnlyBanner darkMode={darkMode} />}
    <Card className={`border-0 shadow-soft rounded-3xl ${cardBg}`}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className={darkMode ? "text-gray-100" : ""}>Promotional Codes</CardTitle>
        {!viewOnly && (
        <Button onClick={() => { setEditingPromo(null); setPromoForm(initialPromoForm); setPromoDialogOpen(true); }} className="rounded-xl bg-primary" data-testid="add-promo-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Promo Code
        </Button>
        )}
      </CardHeader>
      <CardContent>
        {promoCodes.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {promoCodes.filter(p => !adminSearch || p.code?.toLowerCase().includes(adminSearch.toLowerCase())).map(promo => (
              <Card key={promo.id} className={`border-2 rounded-2xl ${promo.is_active ? (darkMode ? 'border-green-700/50 bg-green-900/10' : 'border-success/30 bg-success/5') : (darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50')}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${promo.discount_type === 'percentage' ? (darkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-accent/10 text-accent') : (darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-primary/10 text-primary')}`}>
                        {promo.discount_type === 'percentage' ? <Percent className="w-5 h-5" /> : <DollarSign className="w-5 h-5" />}
                      </div>
                      <div>
                        <p className={`font-bold text-lg font-mono ${darkMode ? "text-white" : ""}`}>{promo.code}</p>
                        <p className={`text-xs ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>
                          {promo.discount_type === 'percentage' ? `${promo.discount_value}% off` : `GHS ${promo.discount_value} off`}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="icon" className={`rounded-lg ${darkMode ? "bg-gray-700/50 border-gray-600 text-gray-200 hover:bg-gray-600 hover:text-white" : ""}`} onClick={() => copyPromoCode(promo.code)} data-testid={`copy-promo-${promo.id}`}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className={`space-y-2 text-sm mb-4 ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>
                    {promo.min_order_amount && <p>Min order: {formatPrice(promo.min_order_amount)}</p>}
                    {promo.max_uses && <p>Uses: {promo.use_count || 0} / {promo.max_uses}</p>}
                    {promo.first_order_only && <Badge variant="outline" className={`text-xs ${darkMode ? "border-gray-600 text-gray-300" : ""}`}>First order only</Badge>}
                    {promo.valid_until && (
                      <p className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Expires: {new Date(promo.valid_until).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  
                  <div className={`flex items-center justify-between pt-3 border-t ${darkMode ? "border-gray-700" : ""}`}>
                    <Badge variant={promo.is_active ? "default" : "secondary"} className={`rounded-full ${promo.is_active ? 'bg-success text-white' : ''}`}>
                      {promo.is_active ? <><CheckCircle className="w-3 h-3 mr-1" /> Active</> : "Inactive"}
                    </Badge>
                    <div className="flex gap-1">
                      {!viewOnly && (
                      <>
                      <Button variant="outline" size="sm" className={`rounded-lg ${darkMode ? "bg-gray-700/50 border-gray-600 text-gray-200 hover:bg-gray-600 hover:text-white" : ""}`} onClick={() => openEditPromo(promo)} data-testid={`edit-promo-${promo.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" className={`rounded-lg ${darkMode ? "text-red-400 hover:text-red-300 border-gray-600 hover:bg-red-900/20" : "text-destructive hover:text-destructive hover:bg-red-50"}`} onClick={() => handleDeletePromo(promo.id)} data-testid={`delete-promo-${promo.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                      </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Tag className={`w-16 h-16 mx-auto mb-4 ${darkMode ? "text-gray-600" : "text-muted-foreground"}`} />
            <p className={`mb-4 ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>No promo codes yet</p>
            <Button onClick={() => setPromoDialogOpen(true)} className="rounded-xl">
              <Plus className="w-4 h-4 mr-2" /> Create First Promo Code
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
};
